import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowRight, Minus, Plus, ShoppingBag, Trash2, X } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useCart } from "../lib/cart";
import { useCurrency, GBP_RATE } from "../lib/currency";

export const CartDrawer = () => {
  const { items, open, setOpen, updateQty, removeItem, subtotal, count } = useCart();
  const { currency, format } = useCurrency();
  const navigate = useNavigate();

  const goEnquiry = () => {
    setOpen(false);
    navigate("/contact");
  };

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            key="overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
            onClick={() => setOpen(false)}
            className="fixed inset-0 z-[60] bg-slate-950/50 backdrop-blur-sm"
          />
          <motion.aside
            key="panel"
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ type: "spring", damping: 30, stiffness: 300 }}
            data-testid="cart-drawer"
            className="fixed right-0 top-0 z-[70] flex h-full w-full max-w-md flex-col bg-white shadow-2xl"
          >
            <div className="flex items-center justify-between border-b border-slate-200 px-6 py-5">
              <h2 className="flex items-center gap-2 font-display text-lg font-bold text-slate-900">
                <ShoppingBag className="h-5 w-5 text-emerald-700" />
                Your Enquiry Basket
                <span data-testid="cart-drawer-count" className="rounded-full bg-emerald-50 px-2 py-0.5 font-mono text-xs font-bold text-emerald-800">
                  {count}
                </span>
              </h2>
              <button
                type="button"
                data-testid="cart-close-button"
                aria-label="Close basket"
                onClick={() => setOpen(false)}
                className="rounded-full p-2 text-slate-500 transition-[background-color,color] hover:bg-slate-100 hover:text-slate-900"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto px-6 py-4">
              {items.length === 0 ? (
                <div className="flex h-full flex-col items-center justify-center gap-3 text-center">
                  <ShoppingBag className="h-10 w-10 text-slate-300" />
                  <p className="text-sm text-slate-500">
                    Your basket is empty. Add products from the catalogue to include them in your enquiry.
                  </p>
                </div>
              ) : (
                <ul className="space-y-4">
                  {items.map((item) => (
                    <li key={item.id} className="flex gap-3 rounded-xl border border-slate-200 p-3" data-testid={`cart-item-${item.id}`}>
                      <img src={item.image} alt={`${item.name} pack`} className="h-16 w-16 rounded-lg object-cover" />
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-sm font-semibold text-slate-900">
                          {item.name} {item.strength}
                        </p>
                        <p className="font-mono text-xs text-slate-500">{item.pack_size}</p>
                        <div className="mt-2 flex items-center justify-between">
                          <div className="flex items-center rounded-lg border border-slate-200">
                            <button type="button" data-testid={`cart-qty-minus-${item.id}`} aria-label="Decrease" onClick={() => updateQty(item.id, item.quantity - 1)} className="px-2 py-1 text-slate-500 hover:text-emerald-700">
                              <Minus className="h-3 w-3" />
                            </button>
                            <span className="w-5 text-center font-mono text-xs font-bold">{item.quantity}</span>
                            <button type="button" data-testid={`cart-qty-plus-${item.id}`} aria-label="Increase" onClick={() => updateQty(item.id, item.quantity + 1)} className="px-2 py-1 text-slate-500 hover:text-emerald-700">
                              <Plus className="h-3 w-3" />
                            </button>
                          </div>
                          <span className="font-mono text-sm font-bold text-emerald-800">
                            {format(item.price * item.quantity)}
                          </span>
                        </div>
                      </div>
                      <button type="button" data-testid={`cart-remove-${item.id}`} aria-label="Remove item" onClick={() => removeItem(item.id)} className="self-start rounded-full p-1.5 text-slate-400 transition-[color] hover:text-red-600">
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            {items.length > 0 && (
              <div className="border-t border-slate-200 px-6 py-5">
                <div className="mb-1 flex items-center justify-between">
                  <span className="text-sm text-slate-500">Displayed subtotal</span>
                  <span data-testid="cart-subtotal" className="font-mono text-lg font-bold text-slate-900">
                    {format(subtotal)}
                  </span>
                </div>
                <p className="mb-4 text-xs leading-relaxed text-slate-400">
                  {currency === "GBP"
                    ? `Pound prices are converted automatically at $1 ≈ £${GBP_RATE}. `
                    : ""}
                  Displayed prices are indicative. Our team confirms availability, packs and supply eligibility by email — checkout completes as an enquiry, not a payment.
                </p>
                <button
                  type="button"
                  data-testid="cart-checkout-button"
                  onClick={goEnquiry}
                  className="flex w-full items-center justify-center gap-2 rounded-xl bg-[#0D5C46] px-4 py-3.5 font-display text-sm font-bold text-white transition-[background-color,transform] hover:bg-[#0A4837] active:scale-[0.99]"
                >
                  Checkout — Send Enquiry
                  <ArrowRight className="h-4 w-4" />
                </button>
              </div>
            )}
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  );
};
